import { useMemo } from "react";
import { Percent, CalendarClock, DollarSign, Building2, AlertTriangle, TrendingUp } from "lucide-react";

/**
 * Commercial Loans Analytics panel — pure-frontend portfolio KPIs.
 * No new API call; every metric is computed from the loans array the page
 * already has loaded. Metrics surfaced:
 *   • Weighted-Average Cost of Capital (WACC) — principal-weighted interest rate
 *   • Weighted-Average Maturity (WAM) — years until avg loan matures
 *   • Annual debt service — totalMonthly × 12
 *   • Largest lender exposure — top lender's % of total principal (concentration risk)
 *   • Maturity ladder — % of principal maturing in 0-1y, 1-3y, 3-5y, 5y+
 *   • Lender concentration list — top 5 lenders by principal (bar chart)
 *   • Rate-type mix — Amortizing / Interest-only / IO-then-Am (bar chart)
 */
export default function LoansAnalyticsPanel({ loans }) {
  const m = useMemo(() => computeAnalytics(loans || []), [loans]);

  if (!loans || loans.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="loans-analytics-panel">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="overline text-slate-500 mb-1">Portfolio analytics</div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Debt Stack Intelligence
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Weighted metrics, maturity ladder, and concentration — computed live from {loans.length} active facilities.
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Percent} label="Weighted avg rate (WACC)" value={m.wacc.toFixed(2) + "%"} hint="Principal-weighted" testid="kpi-wacc" />
        <Kpi icon={CalendarClock} label="Weighted avg maturity" value={m.wam.toFixed(1) + " yrs"} hint={`${m.maturingSoon} loan${m.maturingSoon === 1 ? "" : "s"} < 12mo`} tone={m.maturingSoon > 0 ? "amber" : "slate"} testid="kpi-wam" />
        <Kpi icon={DollarSign} label="Annual debt service" value={fmt(m.annualDebtService)} hint={fmt(m.monthlyDebtService) + "/mo"} testid="kpi-annual-debt-service" />
        <Kpi icon={Building2} label="Top lender exposure" value={m.topLenderPct.toFixed(0) + "%"} hint={m.topLenderName || "—"} tone={m.topLenderPct > 60 ? "amber" : "slate"} testid="kpi-concentration" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Maturity ladder */}
        <div data-testid="maturity-ladder">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-4 h-4 text-slate-600" />
            <div className="overline text-slate-600">Maturity ladder</div>
          </div>
          <div className="space-y-2">
            {m.ladder.map((b) => (
              <LadderBar key={b.bucket} bucket={b.bucket} principal={b.principal} pct={b.pct} count={b.count} totalDebt={m.totalDebt} />
            ))}
          </div>
          {m.maturingSoon > 0 && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 flex items-start gap-2" data-testid="maturity-ladder-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {fmt(m.maturingSoonPrincipal)} in principal matures within 12 months. Start refinancing conversations 6-9 months ahead.
              </span>
            </div>
          )}
        </div>

        {/* Lender concentration + rate-type mix stacked */}
        <div className="space-y-6">
          <div data-testid="lender-concentration">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-slate-600" />
              <div className="overline text-slate-600">Top lenders by principal</div>
            </div>
            {m.lenders.length === 0 ? (
              <div className="text-xs text-slate-400 italic">No lender tagged on any loan yet.</div>
            ) : (
              <div className="space-y-1.5">
                {m.lenders.slice(0, 5).map((l) => (
                  <div key={l.lender} className="flex items-center gap-3 text-xs" data-testid={`lender-row-${l.lender.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="w-36 shrink-0 truncate text-slate-700 font-medium" title={l.lender}>{l.lender}</div>
                    <div className="flex-1 h-2 bg-slate-100 rounded-sm overflow-hidden">
                      <div className="h-full bg-slate-900" style={{ width: `${l.pct}%` }} />
                    </div>
                    <div className="w-24 text-right font-mono text-slate-600 shrink-0">{fmt(l.principal)}</div>
                    <div className="w-10 text-right font-mono text-slate-500 shrink-0">{l.pct.toFixed(0)}%</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid="rate-type-mix">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-slate-600" />
              <div className="overline text-slate-600">Rate-type mix</div>
            </div>
            <div className="flex rounded-sm overflow-hidden h-3 border border-slate-200">
              {m.rateTypes.map((t) => (
                <div
                  key={t.label}
                  className={t.color}
                  style={{ width: `${t.pct}%` }}
                  title={`${t.label}: ${t.pct.toFixed(0)}% (${fmt(t.principal)})`}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-xs">
              {m.rateTypes.map((t) => (
                <div key={t.label} className="flex items-center gap-1.5" data-testid={`rate-type-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <span className={`w-2.5 h-2.5 rounded-sm ${t.color}`} />
                  <span className="text-slate-700">{t.label}</span>
                  <span className="text-slate-400 font-mono">{t.pct.toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const toneClass =
    tone === "amber"
      ? "border-amber-200 bg-amber-50"
      : "border-slate-200 bg-white";
  return (
    <div className={`border rounded-sm p-4 ${toneClass}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-bold text-slate-900 font-mono mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function LadderBar({ bucket, principal, pct, count, totalDebt }) {
  return (
    <div className="flex items-center gap-3 text-xs" data-testid={`ladder-bar-${bucket.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
      <div className="w-16 shrink-0 font-mono text-slate-700">{bucket}</div>
      <div className="flex-1 h-2 bg-slate-100 rounded-sm overflow-hidden">
        <div
          className="h-full bg-emerald-700"
          style={{ width: totalDebt ? `${(principal / totalDebt) * 100}%` : "0%" }}
        />
      </div>
      <div className="w-24 text-right font-mono text-slate-600 shrink-0">{fmt(principal)}</div>
      <div className="w-10 text-right font-mono text-slate-500 shrink-0">{count}</div>
    </div>
  );
}

// -- math ------------------------------------------------------------------

function computeAnalytics(loans) {
  const totalDebt = loans.reduce((s, l) => s + Number(l.loan_amount || 0), 0);
  const monthlyDebtService = loans.reduce((s, l) => s + Number(l.monthly_payment || 0), 0);
  const annualDebtService = monthlyDebtService * 12;

  // Weighted-Average Cost of Capital (WACC): Σ(principal_i × rate_i) / Σ(principal_i)
  const wacc = totalDebt
    ? loans.reduce((s, l) => s + Number(l.loan_amount || 0) * Number(l.interest_rate || 0), 0) / totalDebt
    : 0;

  // Weighted-Average Maturity (WAM): Σ(principal_i × years_to_maturity_i) / Σ(principal_i)
  const now = new Date();
  const yearsToMaturity = (l) => {
    if (!l.loan_date) return Number(l.term_years || 0);
    const start = new Date(l.loan_date);
    const termMonths = Number(l.term_years || 0) * 12 + Number(l.term_months || 0);
    const maturityMs = start.getTime() + termMonths * 30.44 * 24 * 60 * 60 * 1000;
    return Math.max(0, (maturityMs - now.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };
  const wam = totalDebt
    ? loans.reduce((s, l) => s + Number(l.loan_amount || 0) * yearsToMaturity(l), 0) / totalDebt
    : 0;

  // Maturity ladder
  const buckets = [
    { bucket: "0-1 yr", min: 0, max: 1 },
    { bucket: "1-3 yr", min: 1, max: 3 },
    { bucket: "3-5 yr", min: 3, max: 5 },
    { bucket: "5+ yr", min: 5, max: Infinity },
  ];
  const ladder = buckets.map((b) => {
    const matched = loans.filter((l) => {
      const y = yearsToMaturity(l);
      return y >= b.min && y < b.max;
    });
    const principal = matched.reduce((s, l) => s + Number(l.loan_amount || 0), 0);
    return {
      bucket: b.bucket,
      principal,
      pct: totalDebt ? (principal / totalDebt) * 100 : 0,
      count: matched.length,
    };
  });
  const maturingSoonLoans = loans.filter((l) => yearsToMaturity(l) < 1);
  const maturingSoon = maturingSoonLoans.length;
  const maturingSoonPrincipal = maturingSoonLoans.reduce((s, l) => s + Number(l.loan_amount || 0), 0);

  // Lender concentration
  const byLender = {};
  loans.forEach((l) => {
    const k = (l.lender || "").trim() || "Unspecified";
    byLender[k] = (byLender[k] || 0) + Number(l.loan_amount || 0);
  });
  const lenders = Object.entries(byLender)
    .map(([lender, principal]) => ({
      lender,
      principal,
      pct: totalDebt ? (principal / totalDebt) * 100 : 0,
    }))
    .sort((a, b) => b.principal - a.principal);
  const topLender = lenders[0];
  const topLenderPct = topLender?.pct || 0;
  const topLenderName = topLender?.lender || "";

  // Rate-type mix
  const typeMap = { amortizing: 0, interest_only: 0, io_then_am: 0 };
  loans.forEach((l) => {
    const t = (l.loan_type || "amortizing").toLowerCase();
    typeMap[t] = (typeMap[t] || 0) + Number(l.loan_amount || 0);
  });
  const rateTypes = [
    { label: "Amortizing", key: "amortizing", color: "bg-slate-900" },
    { label: "Interest-only", key: "interest_only", color: "bg-sky-600" },
    { label: "IO → Amortizing", key: "io_then_am", color: "bg-indigo-600" },
  ].map((t) => ({
    ...t,
    principal: typeMap[t.key] || 0,
    pct: totalDebt ? ((typeMap[t.key] || 0) / totalDebt) * 100 : 0,
  })).filter((t) => t.principal > 0);

  return {
    totalDebt,
    monthlyDebtService,
    annualDebtService,
    wacc,
    wam,
    ladder,
    maturingSoon,
    maturingSoonPrincipal,
    lenders,
    topLenderPct,
    topLenderName,
    rateTypes,
  };
}

function fmt(n) {
  if (!n) return "$0";
  return "$" + Math.round(n).toLocaleString();
}
